import {useQuery, type QueryClient, type UseQueryOptions} from '@github-ui/react-query'
import {FetchCAPIError} from './fetch-capi'
import {useConsecutiveCAPI401sContext} from './ConsecutiveCAPI401sContext'
import {getAgentSessionsTokenProvider} from '@github-ui/copilot-auth-token/agent-sessions-token'
import {useEffect, useRef} from 'react'

const MAX_401_RETRIES = 5

export function useCAPIQuery<TData, TSelected = TData>(
  options: UseQueryOptions<TData, Error, TSelected>,
  queryClient?: QueryClient,
) {
  const result = useQuery<TData, Error, TSelected>(options, queryClient)
  const {numberOf401s, increment401s, reset401s} = useConsecutiveCAPI401sContext()
  const {isError, isLoading, error} = result

  // Using this to fix an infinite loop in our effect below by tracking
  // whether we've already processed a specific error.
  const processedErrorRef = useRef<Error | null>(null)

  useEffect(() => {
    // Why are we doing this? It's possible for the tokens we use to contact CAPI
    // to become unusable before their actual expiration time. This happens when a user
    // has too many concurrent tokens. This logic clears the cached token if it starts
    // returning 401s, demonstrating it is no longer usable.
    if (isError && error instanceof FetchCAPIError && error.status === 401) {
      if (processedErrorRef.current === error) {
        return
      }

      // If we've already reached the max number of consecutive 401s, force a new token
      if (numberOf401s >= MAX_401_RETRIES) {
        const tokenProvider = getAgentSessionsTokenProvider()
        tokenProvider.tryRefreshToken()
        processedErrorRef.current = error
        reset401s()
      } else {
        // Otherwise increment the count
        processedErrorRef.current = error
        increment401s()
      }
    } else if (!isError && !isLoading && numberOf401s > 0) {
      // If the query was successful, reset the count if there were any previous failures
      processedErrorRef.current = null
      reset401s()
    } else if (!isError) {
      processedErrorRef.current = null
    }
  }, [isError, isLoading, error, increment401s, reset401s, numberOf401s])

  return result
}
